
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { X, Plus, AlertTriangle, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { pb } from '@/lib/pocketbase';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface StudentData {
  id: string;
  user: string;
  name: string;
  bio?: string;
  grade?: string;
  subjects: string[];
  learningGoals?: string;
  preferredLearningStyle?: string;
  parentEmail?: string;
  emergencyContact?: string;
  personalEmail?: string;
  phoneNumber?: string;
  profilePicture?: string;
  profilePictureUrl?: string;
}

interface EditStudentProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentData: StudentData;
  onSave?: () => void;
}

const EditStudentProfileModal = ({ isOpen, onClose, studentData, onSave }: EditStudentProfileModalProps) => {
  const [formData, setFormData] = useState(studentData);
  const [newSubject, setNewSubject] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const { toast } = useToast();

  // Predefined options for dropdowns
  const availableSubjects = [
    'Mathematics', 'English', 'Spanish', 'French', 'German', 'Chinese', 'Physics', 
    'Chemistry', 'Biology', 'Computer Science', 'History', 'Geography', 'Economics',
    'Psychology', 'Philosophy', 'Art', 'Music', 'Literature', 'Business', 'Accounting',
    'Statistics', 'Calculus', 'Algebra', 'Geometry', 'Trigonometry'
  ];

  const learningStyles = [
    'Visual Learner', 'Auditory Learner', 'Kinesthetic Learner', 'Reading/Writing Learner',
    'Multimodal Learner', 'Sequential Learner', 'Global Learner'
  ];

  const gradeOptions = [
    'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', 'College', 'Graduate'
  ];

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addSubject = () => {
    if (newSubject && !formData.subjects.includes(newSubject)) {
      setFormData(prev => ({
        ...prev,
        subjects: [...prev.subjects, newSubject]
      }));
      setNewSubject('');
    }
  };

  const removeSubject = (subject: string) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.filter(s => s !== subject)
    }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Error",
          description: "File size must be less than 5MB",
          variant: "destructive"
        });
        return;
      }
      setProfilePictureFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const getCurrentPhotoUrl = () => {
    if (previewUrl) return previewUrl;
    if (formData.profilePictureUrl && formData.profilePictureUrl.trim()) {
      const url = formData.profilePictureUrl.trim();
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
    }
    if (formData.profilePicture && formData.profilePicture.trim()) {
      try {
        return pb.files.getURL(studentData, formData.profilePicture);
      } catch (error) {
        return '';
      }
    }
    return '';
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('bio', formData.bio || '');
      formDataToSend.append('grade', formData.grade || '');
      formDataToSend.append('subjects', JSON.stringify(formData.subjects));
      formDataToSend.append('learningGoals', formData.learningGoals || '');
      formDataToSend.append('preferredLearningStyle', formData.preferredLearningStyle || '');
      formDataToSend.append('parentEmail', formData.parentEmail || '');
      formDataToSend.append('emergencyContact', formData.emergencyContact || '');
      formDataToSend.append('personalEmail', formData.personalEmail || '');
      formDataToSend.append('phoneNumber', formData.phoneNumber || '');
      
      if (profilePictureFile) {
        formDataToSend.append('profilePicture', profilePictureFile);
      }

      await pb.collection('student_profiles').update(studentData.id, formDataToSend);
      
      toast({
        title: "Success",
        description: "Profile updated successfully!"
      });
      
      onSave?.();
      onClose();
    } catch (error: any) {
      console.error('Error saving profile:', error);
      
      // Extract meaningful error message
      let errorMessage = "Failed to update profile";
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      }
      
      toast({
        title: "Error Updating Profile",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Student Profile</DialogTitle>
          <DialogDescription>
            Update your student profile information
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Profile Picture Upload */}
          <div>
            <Label>Profile Picture</Label>
            <div className="flex items-center gap-4 mt-2">
              <Avatar className="h-20 w-20">
                <AvatarImage src={getCurrentPhotoUrl()} alt={formData.name} />
                <AvatarFallback>{formData.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Input
                  id="profilePicture"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
                <Label htmlFor="profilePicture" className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent transition-colors">
                    <Upload className="h-4 w-4" />
                    <span className="text-sm">Upload Photo</span>
                  </div>
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Max 5MB. Supported formats: JPG, PNG, WEBP
                </p>
              </div>
            </div>
          </div>

          {/* Name Field */}
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Your full name"
            />
          </div>

          {/* Contact Information Section */}
          <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <Label className="font-medium text-amber-800">Contact Information (Admin Only)</Label>
            </div>
            <p className="text-xs text-amber-700 mb-4">
              This information is only visible to administrators for verification and support purposes. It will not be displayed publicly.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="personalEmail">Personal Email</Label>
                <Input
                  id="personalEmail"
                  type="email"
                  value={formData.personalEmail || ''}
                  onChange={(e) => handleInputChange('personalEmail', e.target.value)}
                  placeholder="your.personal@email.com"
                />
              </div>
              <div>
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber || ''}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Grade and Bio */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="grade">Grade Level</Label>
              <Select value={formData.grade || ''} onValueChange={(value) => handleInputChange('grade', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select grade" />
                </SelectTrigger>
                <SelectContent>
                  {gradeOptions.map((grade) => (
                    <SelectItem key={grade} value={grade}>
                      {grade === 'K' ? 'Kindergarten' : `Grade ${grade}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="learningStyle">Learning Style</Label>
              <Select value={formData.preferredLearningStyle || ''} onValueChange={(value) => handleInputChange('preferredLearningStyle', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select learning style" />
                </SelectTrigger>
                <SelectContent>
                  {learningStyles.map((style) => (
                    <SelectItem key={style} value={style}>
                      {style}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="bio">About Me <span className="text-red-500 ml-1">*</span></Label>
            <Textarea
              id="bio"
              value={formData.bio || ''}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              placeholder="Tell us about yourself, your interests, and what you're hoping to achieve"
              className="min-h-[100px]"
              required
            />
          </div>

          {/* Subjects */}
          <div>
            <Label>Subjects of Interest</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.subjects.map((subject) => (
                <Badge key={subject} variant="default" className="flex items-center gap-1">
                  {subject}
                  <X 
                    className="h-3 w-3 cursor-pointer" 
                    onClick={() => removeSubject(subject)}
                  />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Select value={newSubject} onValueChange={setNewSubject}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a subject" />
                </SelectTrigger>
                <SelectContent>
                  {availableSubjects
                    .filter(subject => !formData.subjects.includes(subject))
                    .map((subject) => (
                    <SelectItem key={subject} value={subject}>
                      {subject}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" onClick={addSubject} size="sm" disabled={!newSubject}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="learningGoals">Learning Goals</Label>
            <Textarea
              id="learningGoals"
              value={formData.learningGoals || ''}
              onChange={(e) => handleInputChange('learningGoals', e.target.value)}
              placeholder="What do you want to achieve through tutoring?"
              className="min-h-[80px]"
            />
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="parentEmail">Parent Email (Optional)</Label>
              <Input
                id="parentEmail"
                type="email"
                value={formData.parentEmail || ''}
                onChange={(e) => handleInputChange('parentEmail', e.target.value)}
                placeholder="parent@example.com"
              />
            </div>
            <div>
              <Label htmlFor="emergencyContact">Emergency Contact (Optional)</Label>
              <Input
                id="emergencyContact"
                value={formData.emergencyContact || ''}
                onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                placeholder="Emergency contact information"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditStudentProfileModal;
